import apiClient from "./apiClient";
import {
  PolicyAcknowledgementRead,
  PolicyAcknowledgementStatusRead,
  TenantPolicyListQuery,
  TenantPolicyListResponse,
  PolicyVersionApprovalRead,
  TenantPolicyDetail,
  TenantPolicyDraftUpdatePayload,
  TenantPolicyRead,
  TenantPolicyRetirePayload,
  TenantPolicySettingsUpdatePayload,
  TenantPolicyVersionRead,
} from "../types/tenantPolicy";

const toPolicyId = (tenantPolicyId: number | string) => {
  const numericId = Number(tenantPolicyId);
  return Number.isNaN(numericId) ? String(tenantPolicyId) : String(numericId);
};

export const listTenantPoliciesRegister = async (
  params: TenantPolicyListQuery = {}
): Promise<TenantPolicyListResponse> => {
  const response = await apiClient.get<TenantPolicyListResponse | TenantPolicyRead[]>("/tenant/policies", {
    params: {
      q: params.q || undefined,
      status: params.status || undefined,
      published: typeof params.published === "boolean" ? params.published : undefined,
      review_due: typeof params.review_due === "boolean" ? params.review_due : undefined,
      limit: params.limit,
      offset: params.offset,
    },
  });
  const payload = response.data;
  if (Array.isArray(payload)) {
    const fallbackItems = payload;
    const fallbackLimit = params.limit ?? fallbackItems.length;
    const fallbackOffset = params.offset ?? 0;
    return {
      items: fallbackItems,
      total: fallbackItems.length,
      limit: fallbackLimit,
      offset: fallbackOffset,
    };
  }
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    total: Number.isFinite(payload?.total) ? payload.total : 0,
    limit: Number.isFinite(payload?.limit) ? payload.limit : params.limit ?? 20,
    offset: Number.isFinite(payload?.offset) ? payload.offset : params.offset ?? 0,
  };
};

export const listTenantPolicies = async (): Promise<TenantPolicyRead[]> => {
  const limit = 200;
  let offset = 0;
  let total = Number.MAX_SAFE_INTEGER;
  const items: TenantPolicyRead[] = [];

  while (items.length < total) {
    const page = await listTenantPoliciesRegister({ limit, offset });
    items.push(...page.items);
    total = page.total;
    if (page.items.length === 0) {
      break;
    }
    offset += page.limit;
  }

  return items;
};

export const getTenantPolicy = async (
  tenantPolicyId: number | string
): Promise<TenantPolicyDetail> => {
  const response = await apiClient.get<TenantPolicyDetail>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}`
  );
  return response.data;
};

export const updateTenantPolicySettings = async (
  tenantPolicyId: number | string,
  payload: TenantPolicySettingsUpdatePayload
): Promise<TenantPolicyDetail> => {
  const response = await apiClient.patch<TenantPolicyDetail>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}`,
    payload
  );
  return response.data;
};

export const createTenantPolicy = async (
  payload: { name: string; description?: string | null; owner_id?: string | null }
): Promise<TenantPolicyDetail> => {
  const response = await apiClient.post<TenantPolicyDetail>("/tenant/policies/", {
    name: payload.name,
    description: payload.description ?? null,
    owner_id: payload.owner_id ?? null,
  });
  return response.data;
};

export const listTenantPolicyVersions = async (
  tenantPolicyId: number | string
): Promise<TenantPolicyVersionRead[]> => {
  const response = await apiClient.get<TenantPolicyVersionRead[]>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions`
  );
  return Array.isArray(response.data) ? response.data : [];
};

export const createTenantPolicyDraft = async (
  tenantPolicyId: number | string
): Promise<TenantPolicyVersionRead> => {
  const response = await apiClient.post<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/draft`
  );
  return response.data;
};

export const updateTenantPolicyDraft = async (
  tenantPolicyId: number | string,
  versionId: number | string,
  payload: TenantPolicyDraftUpdatePayload
): Promise<TenantPolicyVersionRead> => {
  const response = await apiClient.patch<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}`,
    payload
  );
  return response.data;
};

export const uploadTenantPolicyVersionDocument = async (
  tenantPolicyId: number | string,
  versionId: number | string,
  file: File
): Promise<TenantPolicyVersionRead> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/document`,
    formData
  );
  return response.data;
};

export const deleteTenantPolicyVersionDocument = async (
  tenantPolicyId: number | string,
  versionId: number | string
): Promise<TenantPolicyVersionRead> => {
  const response = await apiClient.delete<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/document`
  );
  return response.data;
};

export const getTenantPolicyVersionDocumentUrl = (
  tenantPolicyId: number | string,
  versionId: number | string
): string =>
  `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
    toPolicyId(versionId)
  )}/document`;

export const submitTenantPolicyVersion = async (
  tenantPolicyId: number | string,
  versionId: number | string
): Promise<TenantPolicyVersionRead> => {
  const response = await apiClient.post<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/submit`
  );
  return response.data;
};

export const approveTenantPolicyVersion = async (
  tenantPolicyId: number | string,
  versionId: number | string,
  comment?: string | null
): Promise<PolicyVersionApprovalRead> => {
  const response = await apiClient.post<PolicyVersionApprovalRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/approve`,
    { comment: comment ?? null }
  );
  return response.data;
};

export const rejectTenantPolicyVersion = async (
  tenantPolicyId: number | string,
  versionId: number | string,
  comment?: string | null
): Promise<PolicyVersionApprovalRead> => {
  const response = await apiClient.post<PolicyVersionApprovalRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/reject`,
    { comment: comment ?? null }
  );
  return response.data;
};

export const publishTenantPolicyVersion = async (
  tenantPolicyId: number | string,
  versionId: number | string
): Promise<TenantPolicyVersionRead> => {
  const response = await apiClient.post<TenantPolicyVersionRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/publish`
  );
  return response.data;
};

export const retireTenantPolicy = async (
  tenantPolicyId: number | string,
  payload: TenantPolicyRetirePayload
): Promise<TenantPolicyDetail> => {
  const response = await apiClient.post<TenantPolicyDetail>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/retire`,
    payload
  );
  return response.data;
};

export const acknowledgeTenantPolicyVersion = async (
  tenantPolicyId: number | string,
  versionId: number | string,
  source = "ui"
): Promise<PolicyAcknowledgementRead> => {
  const response = await apiClient.post<PolicyAcknowledgementRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/acknowledge`,
    { source }
  );
  return response.data;
};

export const getTenantPolicyAcknowledgementStatus = async (
  tenantPolicyId: number | string
): Promise<PolicyAcknowledgementStatusRead> => {
  const response = await apiClient.get<PolicyAcknowledgementStatusRead>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/acknowledgement-status`
  );
  return response.data;
};

export const listTenantPolicyVersionAcknowledgements = async (
  tenantPolicyId: number | string,
  versionId: number | string
): Promise<PolicyAcknowledgementRead[]> => {
  const response = await apiClient.get<PolicyAcknowledgementRead[]>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/acknowledgements`
  );
  return Array.isArray(response.data) ? response.data : [];
};

export const listTenantPolicyVersionApprovals = async (
  tenantPolicyId: number | string,
  versionId: number | string
): Promise<PolicyVersionApprovalRead[]> => {
  const response = await apiClient.get<PolicyVersionApprovalRead[]>(
    `/tenant/policies/${encodeURIComponent(toPolicyId(tenantPolicyId))}/versions/${encodeURIComponent(
      toPolicyId(versionId)
    )}/approvals`
  );
  return Array.isArray(response.data) ? response.data : [];
};
